import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { fetchReviews, postReview } from '../api';
import { toast } from 'sonner';

function Stars({ value, size = 16, onChange }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          onClick={() => onChange && onChange(n)}
          className={onChange ? 'cursor-pointer' : 'cursor-default'}
          aria-label={`${n} star`}
        >
          <Star size={size} className={n <= value ? 'fill-gold text-gold' : 'fill-transparent text-burgundy-900/30'} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ name: '', rating: 5, text: '' });
  const [busy, setBusy] = useState(false);

  const load = () => fetchReviews(productId).then((d) => setReviews(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { setLoaded(false); load(); /* eslint-disable-next-line */ }, [productId]);

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) { toast.error('Please add your name and review'); return; }
    setBusy(true);
    try {
      await postReview(productId, { name: form.name.trim(), rating: form.rating, text: form.text.trim() });
      toast.success('Thank you for your review!');
      setForm({ name: '', rating: 5, text: '' });
      load();
    } catch { toast.error('Could not submit review'); }
    finally { setBusy(false); }
  };

  return (
    <section className="max-w-[1000px] mx-auto px-4 md:px-8 py-16 border-t border-gold/15">
      <div className="text-center mb-10">
        <p className="text-gold-dark tracking-luxe text-xs uppercase mb-3">Client Voices</p>
        <h2 className="font-display text-3xl md:text-4xl text-burgundy-900">Reviews</h2>
        <div className="flex items-center justify-center gap-3 mt-3">
          <Stars value={Math.round(avg)} size={18} />
          <span className="text-sm text-burgundy-900/70">{reviews.length ? `${avg.toFixed(1)} · ${reviews.length} review${reviews.length === 1 ? '' : 's'}` : 'No reviews yet'}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        {/* List */}
        <div className="space-y-5">
          {loaded && reviews.length === 0 && <p className="text-sm text-burgundy-900/50">Be the first to review this piece.</p>}
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-gold/10 pb-5">
              <div className="flex items-center justify-between mb-1.5">
                <p className="font-display text-lg text-burgundy-900">{r.name}</p>
                <Stars value={r.rating} size={14} />
              </div>
              <p className="text-sm text-burgundy-900/70 leading-relaxed">{r.text}</p>
              <p className="text-[11px] text-burgundy-900/40 mt-2">{r.date}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-cream/60 p-7 h-fit">
          <h3 className="font-display text-2xl text-burgundy-900 mb-5">Write a Review</h3>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Your Rating</label>
              <Stars value={form.rating} size={24} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} />
            </div>
            <div>
              <label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Name *</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold" />
            </div>
            <div>
              <label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Your Review *</label>
              <textarea rows={4} value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} className="w-full bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold resize-none" />
            </div>
            <button type="submit" disabled={busy} className="w-full bg-burgundy-900 text-cream py-3.5 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors disabled:opacity-60">
              {busy ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
